import React from 'react';
import PropTypes from 'prop-types';
import {
  Dimmer, Loader, Message, Table,
} from 'semantic-ui-react';

import OnboardingQueueListRow from './ListRow';
import {
  QUEUE_COLUMNS,
  QUEUE_EMPTY_MESSAGE,
  QUEUE_NO_MATCH_MESSAGE,
  QUEUE_REFUSED_MESSAGE,
  QUEUE_VIEW,
  SORT_ORDER,
} from '../../constants/onboardingQueue';
import { queueErrorMessage } from '../../utilities/onboardingQueue';

const SORTED_BY_ORDER = {
  [SORT_ORDER.ASC]: 'ascending',
  [SORT_ORDER.DESC]: 'descending',
};

const OnboardingQueueList = ({
  view,
  items,
  total,
  loading,
  error,
  refused,
  filtersActive,
  sortOrder,
  onSort,
  onAction,
}) => {
  if (error) {
    return (
      <Message
        negative
        data-testid='queue-error'
        content={queueErrorMessage(error)}
      />
    );
  }

  if (refused) {
    return (
      <Message
        error
        data-testid='queue-refused'
        content={QUEUE_REFUSED_MESSAGE}
      />
    );
  }

  if (!loading && total === 0) {
    return (
      <Message
        info
        data-testid='queue-empty'
        content={filtersActive ? QUEUE_NO_MATCH_MESSAGE : QUEUE_EMPTY_MESSAGE}
      />
    );
  }

  const columns = QUEUE_COLUMNS[view];
  const headerRow = (
    <Table.Row>
      {columns.map(column => (
        <Table.HeaderCell
          key={column.key}
          textAlign={column.textAlign}
          data-testid={`queue-header-${column.key}`}
          sorted={column.sortable ? SORTED_BY_ORDER[sortOrder] : undefined}
          onClick={column.sortable ? onSort : undefined}
        >
          {column.label}
        </Table.HeaderCell>
      ))}
    </Table.Row>
  );

  return (
    <Dimmer.Dimmable dimmed={loading}>
      <Dimmer active={loading} inverted data-testid='queue-dimmer'>
        <Loader data-testid='queue-loading'>Loading...</Loader>
      </Dimmer>

      <Table
        sortable
        celled
        padded
        striped
        size='small'
        compact='very'
        style={{ fontSize: '90%' }}
        headerRow={headerRow}
        tableData={items}
        renderBodyRow={OnboardingQueueListRow(columns, { view, onAction })}
      />
    </Dimmer.Dimmable>
  );
};

OnboardingQueueList.propTypes = {
  view: PropTypes.oneOf(Object.values(QUEUE_VIEW)).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.shape({ message: PropTypes.string }),
  refused: PropTypes.bool,
  filtersActive: PropTypes.bool,
  sortOrder: PropTypes.oneOf(Object.values(SORT_ORDER)),
  onSort: PropTypes.func,
  onAction: PropTypes.func.isRequired,
};

OnboardingQueueList.defaultProps = {
  loading: false,
  error: null,
  refused: false,
  filtersActive: false,
  sortOrder: null,
  onSort: null,
};

export default OnboardingQueueList;
